import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";

import { usePatchRreApprovalByIdMutation } from "services/rre-api";

import {
  useGetRreByIdQuery,
  useGetRreValidationsQuery,
  usePatchRreByIdMutation,
} from "services/rre-api";

import { useToken } from "utils/context";
import { getCmsCommunicationForms } from "forms/rre";
import RREPageContainer from "./RREPageContainer";
import KaboodleForm from "components/Forms/KaboodleForm";
import { useDispatch, useSelector } from "utils/redux";
import { setValidationState } from "reducers/main";
import { setIsLoading } from "reducers/main";
import { KaboodleToaster } from "components/common";

const CmsCommunication = () => {
  const dispatch = useDispatch();
  const { accessToken } = useToken();
  const { rreId } = useParams<{ rreId?: string }>();
  const [showQueryTimeframe, setShowQueryTimeframe] = useState<boolean>();
  const [showClaimTimeframe, setShowClaimTimeframe] = useState<boolean>();
  const [isApprovable, setIsApprovable] = useState<boolean>(true);

  const approval_fields = useMemo(
    () => [
      "rre_profile.is_approved_for_query_submission",
      "rre_profile.is_approved_for_claim_submission",
    ],
    [],
  );

  const { data: rre, isLoading } = useGetRreByIdQuery(
    { rreId },
    { skip: !rreId },
  );

  useEffect(() => {
    dispatch(setIsLoading(isLoading));
  }, [dispatch, isLoading]);

  const [patchCmsCommunication] = usePatchRreByIdMutation();
  const [patchCmsApproval] = usePatchRreApprovalByIdMutation();

  const { data: rreValidation } = useGetRreValidationsQuery(
    { id: rreId },
    { skip: !rreId },
  );

  useEffect(() => {
    if (rreValidation) {
      const validationData = Object.values(rreValidation).flat();
      const errors = validationData.filter((item) => item.level === "ERROR");
      const warnings = validationData.filter((item) => item.level === "WARN");
      dispatch(
        setValidationState({
          errors: errors,
          warnings: warnings,
        }),
      );
    }
  }, [dispatch, rreValidation]);

  const { reset, clearErrors, setError } = useFormContext();
  const constants = useSelector((state) => state.shared.constants);

  useEffect(() => {
    if (rre && constants) {
      reset(rre);
      setShowQueryTimeframe(
        rre.query_file_communication_mode ===
          constants["CMS_COMMUNICATION_MODE"][1]["value"],
      );
      setShowClaimTimeframe(
        rre.claim_file_communication_mode ===
          constants["CMS_COMMUNICATION_MODE"][1]["value"],
      );
      setIsApprovable(rre.rre_profile.is_approvable);
    }
  }, [rre, reset, constants]);

  const handleSave = useCallback(
    async (field: string, value: unknown) => {
      if (rreId && accessToken) {
        try {
          if (approval_fields.includes(field)) {
            await patchCmsApproval({
              rreId: rreId,
              data: { [field.replace(/^rre_profile./, "")]: value },
            }).unwrap();
          } else {
            await patchCmsCommunication({
              rreId: rreId,
              data: { [field]: value },
            }).unwrap();
          }
          clearErrors(field);
          KaboodleToaster.success("Saved Successfully.");
        } catch (error) {
          console.error(
            "Failed to save changes on RRE CMS Communication:",
            error,
          );
          KaboodleToaster.error(error, { duration: 10000 });
        }
      }
    },
    [
      rreId,
      accessToken,
      approval_fields,
      clearErrors,
      patchCmsApproval,
      patchCmsCommunication,
      setError,
    ],
  );

  const CmsCommunicationContent = useMemo(() => {
    const forms = getCmsCommunicationForms(
      constants,
      showQueryTimeframe,
      showClaimTimeframe,
      isApprovable,
    );
    return <KaboodleForm cards={forms[0].cards || []} onSave={handleSave} />;
  }, [
    constants,
    handleSave,
    showQueryTimeframe,
    showClaimTimeframe,
    isApprovable,
  ]);

  return <RREPageContainer>{CmsCommunicationContent}</RREPageContainer>;
};

export default CmsCommunication;
